"use strict";
/**
 * Frontend Web UID Helper
 *
 * Generates and persists a cryptographically secure, collision-resistant
 * 25-30 digit numeric UID for anonymous web chat visitors.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CHATBOT_UID_STORAGE_KEY = void 0;
exports.isValidWebUid = isValidWebUid;
exports.getSecureRandomDigits = getSecureRandomDigits;
exports.generateWebUid = generateWebUid;
exports.getOrCreateWebUid = getOrCreateWebUid;
exports.generateSessionId = generateSessionId;
exports.isValidSessionId = isValidSessionId;
exports.CHATBOT_UID_STORAGE_KEY = 'chatbot_uid';
/**
 * Validates that a UID matches the required 25-30 numeric digits format.
 */
/** Purpose: Provide the is valid web uid operation for the web-uid module. Why: Callers depend on this shared result to keep the surrounding flow consistent. */
function isValidWebUid(uid) {
    if (typeof uid !== 'string')
        return false;
    return /^\d{25,30}$/.test(uid);
}
/**
 * Generates cryptographically secure random digits using Web Crypto API.
 * Uses rejection sampling (< 250) to prevent modulo bias.
 *
 * @param count Number of random digits to generate
 */
/** Purpose: Provide the get secure random digits operation for the web-uid module. Why: Callers depend on this shared result to keep the surrounding flow consistent. */
function getSecureRandomDigits(count) {
    let digits = '';
    /** Purpose: Generate cryptographically random bytes in the browser or Node runtime. Why: UIDs need secure randomness while sharing one implementation across environments. */
    const getBytes = (length) => {
        const cryptoObj = typeof window !== 'undefined' && window.crypto
            ? window.crypto
            : typeof globalThis !== 'undefined' && globalThis.crypto
                ? globalThis.crypto
                : null;
        if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
            const arr = new Uint8Array(length);
            cryptoObj.getRandomValues(arr);
            return arr;
        }
        const arr = new Uint8Array(length);
        for (let i = 0; i < length; i++) {
            arr[i] = Math.floor(Math.random() * 256);
        }
        return arr;
    };
    while (digits.length < count) {
        const needed = count - digits.length;
        const bytes = getBytes(Math.max(needed * 2, 16));
        for (let i = 0; i < bytes.length && digits.length < count; i++) {
            if (bytes[i] < 250) {
                digits += (bytes[i] % 10).toString();
            }
        }
    }
    return digits;
}
/**
 * Generates a persistent unique numeric UID for anonymous web visitors.
 *
 * Format:
 *   Timestamp (13 digits) + 16 cryptographically secure random digits
 *   = 29 digits total (satisfies 25-30 digit requirement)
 *
 * Example:
 *   13483085088073078688140362834
 */
/** Purpose: Resolve generate web uid in the web-uid module. Why: This keeps the surrounding interface operation aligned with the expected data shape. */
function generateWebUid() {
    const timestamp = Date.now().toString(); // 13 digits
    const randomSuffix = getSecureRandomDigits(16); // 16 digits
    return `${timestamp}${randomSuffix}`;
}
/**
 * Retrieves the persistent UID from localStorage or generates, stores, and returns a new one.
 *
 * @param storage Optional custom or mock StorageLike instance. Defaults to window.localStorage.
 */
let inMemoryWebUid = null;
/** Purpose: Reuse a saved web identity or create and persist a new one. Why: Widget conversations need a stable anonymous user ID across visits. */
function getOrCreateWebUid(storage) {
    const targetStorage = storage !== undefined
        ? storage
        : typeof window !== 'undefined' && window.localStorage
            ? window.localStorage
            : null;
    if (targetStorage) {
        try {
            const existing = targetStorage.getItem(exports.CHATBOT_UID_STORAGE_KEY);
            if (existing && isValidWebUid(existing)) {
                inMemoryWebUid = existing;
                return existing;
            }
        }
        catch {
            // Access to localStorage might be restricted
        }
    }
    else if (inMemoryWebUid && isValidWebUid(inMemoryWebUid)) {
        return inMemoryWebUid;
    }
    const newUid = generateWebUid();
    inMemoryWebUid = newUid;
    if (targetStorage) {
        try {
            targetStorage.setItem(exports.CHATBOT_UID_STORAGE_KEY, newUid);
        }
        catch {
            // Quota exceeded or private browsing restriction
        }
    }
    return newUid;
}
/**
 * Generates a 24-character lowercase hexadecimal Session ID.
 * Format: /^[0-9a-f]{24}$/
 */
/** Purpose: Resolve generate session id in the web-uid module. Why: This keeps the surrounding interface operation aligned with the expected data shape. */
function generateSessionId() {
    const cryptoObj = typeof window !== 'undefined' && window.crypto
        ? window.crypto
        : typeof globalThis !== 'undefined' && globalThis.crypto
            ? globalThis.crypto
            : null;
    if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
        const bytes = new Uint8Array(12);
        cryptoObj.getRandomValues(bytes);
        let hex = '';
        for (let i = 0; i < bytes.length; i++) {
            hex += (bytes[i] < 16 ? '0' : '') + bytes[i].toString(16);
        }
        return hex.toLowerCase();
    }
    const hexChars = '0123456789abcdef';
    let result = '';
    for (let j = 0; j < 24; j++) {
        result += hexChars.charAt(Math.floor(Math.random() * 16));
    }
    return result;
}
/**
 * Validates whether a given session ID matches the required 24 lowercase hexadecimal format.
 */
/** Purpose: Provide the is valid session id operation for the web-uid module. Why: Callers depend on this shared result to keep the surrounding flow consistent. */
function isValidSessionId(sessionId) {
    if (typeof sessionId !== 'string')
        return false;
    return /^[0-9a-f]{24}$/.test(sessionId);
}
exports.default = {
    CHATBOT_UID_STORAGE_KEY: exports.CHATBOT_UID_STORAGE_KEY,
    generateWebUid,
    getOrCreateWebUid,
    isValidWebUid,
    generateSessionId,
    isValidSessionId,
};
